import { COLOR_HEX } from '../content/games';

const ZONE_ICON = { animals: '🐾', toys: '🧸', moves: '🚗', still: '🏠' };

// Silhouette of something else. A 2-year-old matches a form to its shadow long
// before they can name it, so this is a picture — not a component.
const SHADOW_FILL = '#2B2B2Bcc';

export function ItemVisual({ item, size = 110 }) {
  if (item.kind === 'shadow') {
    const of = item.of || {};
    if (of.kind === 'img') {
      // NOT a true silhouette. The object webps have an opaque background, so
      // brightness(0) blacks out the whole tile and the child is asked to match
      // a cup to a grey square. A faded, desaturated copy keeps the object
      // recognisable — matching a picture to its washed-out twin is a mechanic
      // a 2-year-old already has. Found by looking at the screen, not by a test.
      return (
        <img src={of.src} alt="" draggable={false}
          style={{ width: size, height: size, objectFit: 'cover', borderRadius: size * 0.28,
            filter: 'grayscale(1) contrast(0.7) brightness(1.08)', opacity: 0.55 }}
          className="pointer-events-none select-none" />
      );
    }
    const path = {
      triangle: <path d="M50 10 L90 84 L10 84 Z" fill={SHADOW_FILL} />,
      square: <rect x="12" y="12" width="76" height="76" rx="16" fill={SHADOW_FILL} />,
      star: <path d="M50 6 L61 38 L95 38 L67 58 L78 92 L50 71 L22 92 L33 58 L5 38 L39 38 Z" fill={SHADOW_FILL} />,
    }[of.shape] || <circle cx="50" cy="50" r="42" fill={SHADOW_FILL} />;
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <ellipse cx="50" cy="93" rx="34" ry="5" fill="#00000012" />
        {path}
      </svg>
    );
  }
  if (item.kind === 'ring') {
    // Distinct radius per step so three rings are three visibly different sizes.
    const r = [44, 34, 25][item.step ?? (item.big === false ? 1 : 0)] || 44;
    const c = COLOR_HEX[item.color] || '#ccc';
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <ellipse cx="50" cy="58" rx={r} ry={r * 0.42} fill={c} />
        <ellipse cx="50" cy="54" rx={r} ry={r * 0.42} fill={c} stroke="#ffffff70" strokeWidth="3" />
        <ellipse cx="50" cy="54" rx={r * 0.36} ry={r * 0.16} fill="#00000030" />
      </svg>
    );
  }
  if (item.kind === 'pole') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <ellipse cx="50" cy="84" rx="38" ry="11" fill="#C99B62" />
        <ellipse cx="50" cy="80" rx="38" ry="11" fill="#E0B378" stroke="#00000018" strokeWidth="2" />
        <rect x="44" y="20" width="12" height="60" rx="6" fill="#C99B62" />
        <circle cx="50" cy="20" r="8" fill="#E0B378" stroke="#00000018" strokeWidth="2" />
      </svg>
    );
  }
  if (item.kind === 'paint') {
    const c = COLOR_HEX[item.color] || '#ccc';
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <ellipse cx="50" cy="92" rx="26" ry="5" fill="#00000015" />
        <path d="M50 18 Q78 44 78 62 Q78 88 50 88 Q22 88 22 62 Q22 44 50 18 Z" fill={c} />
        <ellipse cx="39" cy="52" rx="9" ry="13" fill="#ffffff70" transform="rotate(-20 39 52)" />
      </svg>
    );
  }
  if (item.kind === 'img') {
    return (
      <img src={item.src} alt={item.id} draggable={false}
        style={{ width: size, height: size, objectFit: 'cover', borderRadius: size * 0.28 }}
        className="pointer-events-none select-none" />
    );
  }
  const c = COLOR_HEX[item.color] || '#ccc';
  if (item.kind === 'flower') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        {[0, 60, 120, 180, 240, 300].map((r) => (
          <ellipse key={r} cx="50" cy="28" rx="14" ry="22" fill={c} transform={`rotate(${r} 50 50)`} opacity="0.95" />
        ))}
        <circle cx="50" cy="50" r="14" fill="#FFF3D6" stroke="#00000018" strokeWidth="2" />
      </svg>
    );
  }
  if (item.kind === 'basket') {
    return (
      <svg width={size} height={size * 0.8} viewBox="0 0 100 80" className="pointer-events-none">
        <path d="M10 30 Q50 5 90 30" fill="none" stroke={c} strokeWidth="7" strokeLinecap="round" />
        <path d="M8 32 L92 32 L82 74 Q50 82 18 74 Z" fill={c} />
        <path d="M8 32 L92 32 L89 45 L11 45 Z" fill="#00000022" />
      </svg>
    );
  }
  if (item.kind === 'zone') {
    return (
      <div className="pointer-events-none flex items-center justify-center rounded-3xl"
        style={{ width: size * 1.2, height: size * 0.9, background: `${c}30`, border: `4px dashed ${c}` }}>
        <span style={{ fontSize: size * 0.4 }}>{ZONE_ICON[item.icon] || '⭐'}</span>
      </div>
    );
  }
  if (item.kind === 'fruit') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <circle cx="50" cy="56" r="38" fill={c} />
        <ellipse cx="38" cy="42" rx="11" ry="7" fill="#ffffff70" transform="rotate(-25 38 42)" />
        <path d="M50 20 Q49 8 55 4" fill="none" stroke="#8A5A2B" strokeWidth="6" strokeLinecap="round" />
        <ellipse cx="63" cy="12" rx="12" ry="6" fill="#4ADE80" transform="rotate(-20 63 12)" />
        <ellipse cx="50" cy="95" rx="28" ry="4" fill="#00000015" />
      </svg>
    );
  }
  if (item.kind === 'stone') {
    return (
      <svg width={size} height={size * 0.75} viewBox="0 0 100 75" className="pointer-events-none">
        <ellipse cx="50" cy="40" rx="46" ry="32" fill={c} />
        <ellipse cx="38" cy="28" rx="16" ry="9" fill="#ffffff70" transform="rotate(-15 38 28)" />
        <ellipse cx="50" cy="71" rx="40" ry="4" fill="#00000015" />
      </svg>
    );
  }
  if (item.kind === 'hole') {
    // A small hole has to LOOK small next to a big one — that difference is the
    // whole question in size_nest.
    const k = item.size === 'small' ? 0.6 : 1;
    const hole = {
      circle: <circle cx="50" cy="50" r={30 * k} fill="#3A2E22" />,
      square: <rect x={50 - 28 * k} y={50 - 28 * k} width={56 * k} height={56 * k} rx={10 * k} fill="#3A2E22" />,
      triangle: <path d={`M50 ${50 - 30 * k} L${50 + 32 * k} ${50 + 26 * k} L${50 - 32 * k} ${50 + 26 * k} Z`} fill="#3A2E22" />,
    }[item.shape];
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <rect x="4" y="4" width="92" height="92" rx="18" fill="#C99B62" stroke="#00000022" strokeWidth="3" />
        {hole}
      </svg>
    );
  }
  if (item.kind === 'plate') {
    const pos = [[35, 48], [63, 48], [49, 66], [49, 32]];
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <ellipse cx="50" cy="55" rx="46" ry="30" fill="#fff" stroke="#00000018" strokeWidth="3" />
        <ellipse cx="50" cy="55" rx="34" ry="20" fill="#F2EBDD" />
        {pos.slice(0, item.count).map(([x, y], i) => (
          <g key={i}><circle cx={x} cy={y} r="11" fill={c} /><circle cx={x - 3} cy={y - 4} r="3" fill="#ffffff70" /></g>
        ))}
      </svg>
    );
  }
  if (item.kind === 'tree') {
    const h = item.h || 1;
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <rect x="45" y={96 - 60 * h} width="10" height={60 * h} rx="4" fill="#8A5A2B" />
        <circle cx="50" cy={92 - 60 * h} r={20 + 8 * h} fill="#4ADE80" stroke="#ffffff70" strokeWidth="3" />
        <ellipse cx="50" cy="96" rx="26" ry="4" fill="#00000015" />
      </svg>
    );
  }
  // Long vs short. Horizontal on purpose: 'tall' already occupies the vertical
  // axis in this world, and a 2-year-old reads the two as different questions.
  if (item.kind === 'rope') {
    const l = item.len || 1;
    const half = 44 * l;
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
        <path d={`M${50 - half} 56 Q50 38 ${50 + half} 56`} stroke={c} strokeWidth="15"
          strokeLinecap="round" fill="none" />
        <path d={`M${50 - half} 56 Q50 38 ${50 + half} 56`} stroke="#ffffff55" strokeWidth="5"
          strokeLinecap="round" fill="none" />
      </svg>
    );
  }
  if (item.kind === 'body') {
    const parts = {
      nose: <g><ellipse cx="50" cy="50" rx="34" ry="38" fill="#FFE0B8" /><path d="M50 40 Q58 55 50 62 Q44 58 46 48 Z" fill="#E8A96C" /><circle cx="46" cy="60" r="3" fill="#00000030" /><circle cx="55" cy="60" r="3" fill="#00000030" /></g>,
      eye: <g><ellipse cx="50" cy="50" rx="34" ry="38" fill="#FFE0B8" /><ellipse cx="38" cy="46" rx="10" ry="12" fill="#fff" stroke="#00000030" strokeWidth="2" /><ellipse cx="62" cy="46" rx="10" ry="12" fill="#fff" stroke="#00000030" strokeWidth="2" /><circle cx="38" cy="48" r="5" fill="#3A2E22" /><circle cx="62" cy="48" r="5" fill="#3A2E22" /></g>,
      hand: <g><path d="M35 88 L35 45 Q35 36 42 36 Q47 36 47 44 L47 30 Q47 22 54 22 Q60 22 60 30 L60 42 Q64 34 70 38 Q75 42 71 52 L64 78 Q60 90 48 90 Z" fill="#FFE0B8" stroke="#E8A96C" strokeWidth="3" /></g>,
    };
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">{parts[item.part]}</svg>
    );
  }
  if (item.kind === 'shape') {
    const s = item.size === 'tiny' ? 0.45 : item.size === 'small' ? 0.65 : 1;
    if (item.shape === 'triangle') {
      return (
        <svg width={size * s} height={size * s} viewBox="0 0 100 100" className="pointer-events-none">
          <path d="M50 10 L90 84 L10 84 Z" fill={c} stroke="#ffffff80" strokeWidth="3" strokeLinejoin="round" />
        </svg>
      );
    }
    if (item.shape === 'star') {
      return (
        <svg width={size * s} height={size * s} viewBox="0 0 100 100" className="pointer-events-none">
          <path d="M50 6 L61 38 L95 38 L67 58 L78 92 L50 71 L22 92 L33 58 L5 38 L39 38 Z" fill={c} stroke="#ffffff80" strokeWidth="3" />
        </svg>
      );
    }
    if (item.shape === 'square') {
      return (
        <svg width={size * s} height={size * s} viewBox="0 0 100 100" className="pointer-events-none">
          <rect x="12" y="12" width="76" height="76" rx="16" fill={c} stroke="#ffffff80" strokeWidth="3" />
          <rect x="20" y="18" width="30" height="12" rx="6" fill="#ffffff60" />
        </svg>
      );
    }
    return (
      <svg width={size * s} height={size * s} viewBox="0 0 100 100" className="pointer-events-none">
        <circle cx="50" cy="50" r="42" fill={c} stroke="#ffffff80" strokeWidth="3" />
        <ellipse cx="38" cy="32" rx="12" ry="7" fill="#ffffff80" transform="rotate(-25 38 32)" />
      </svg>
    );
  }
  // ball
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="pointer-events-none">
      <defs>
        <radialGradient id={`g-${item.id}`} cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#ffffffB0" />
          <stop offset="35%" stopColor={c} />
          <stop offset="100%" stopColor={c} />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="45" fill={`url(#g-${item.id})`} />
      <ellipse cx="38" cy="30" rx="12" ry="7" fill="#ffffff90" transform="rotate(-25 38 30)" />
      <ellipse cx="50" cy="94" rx="30" ry="5" fill="#00000015" />
    </svg>
  );
}
